import {
  AuthenticationFactor,
  StytchAPIError,
  StytchSDKAPIError,
} from "@stytch/core/public";
import { useStytchUser } from "@stytch/react";
import { Session, User } from "@stytch/vanilla-js";
import {
  differenceInDays,
  differenceInMinutes,
  isAfter,
  isBefore,
  parseJSON,
} from "date-fns";
import { AuthFactorInstance } from "features/auth/AuthFactor";
import { TFunction } from "i18next";
import { parsePhoneNumber } from "libphonenumber-js";
import log from "loglevel";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FormOnErrorFn } from "shared/components/form/FormTypes";
import { useToast } from "shared/components/toast/useToast";
import { getErrorMessage } from "shared/utils/ErrorUtils";

export const STYTCH_EXTENDED_SESSION_DURATION_DAYS = 30;
const STYTCH_EXTENDED_SESSION_DURATION_MINUTES =
  STYTCH_EXTENDED_SESSION_DURATION_DAYS * 24 * 60; // 30 days

const STYTCH_SESSION_DURATION_MINUTES = 5;
const STYTCH_PRIMARY_AUTH_FACTOR_TYPES = ["password", "magic_link"];

export const STYTCH_REDIRECT_URL = window.location.origin + "/auth/continue";
export const STYTCH_PW_RESET_URL =
  window.location.origin + "/auth/password/reset";

// Stytch session is initially (after pw/magic link auth) set to expire after 30 minutes.
// Later during MFA Auth the session can be extended to 30 days if "remember device" is selected.
// Regardless, password/magic link auth will always expire after 30 minutes (see isStytchPasswordLoginNeeded below).
export const getStytchSessionDurationMinutes = (rememberDevice?: boolean) =>
  rememberDevice
    ? STYTCH_EXTENDED_SESSION_DURATION_MINUTES
    : STYTCH_SESSION_DURATION_MINUTES;

export const isStytchPasswordSetupNeeded = (stytchUser: User) =>
  !stytchUser.password;

/*
 * Returns true if the user needs to re-authenticate with a password or magic link.
 * This is the case if:
 * - User has not authenticated with a password or magic link.
 * - User authenticated with a password or magic link more than 30 minutes ago.
 *   This is always 30 minutes even if "remember device" was selected.
 */
export const isStytchPasswordLoginNeeded = (stytchSession: Session) => {
  const primaryAuthFactors = stytchSession.authentication_factors.filter((f) =>
    STYTCH_PRIMARY_AUTH_FACTOR_TYPES.includes(f.type),
  );
  // If no password or magic link factors, then password login is needed.
  if (!primaryAuthFactors.length) {
    return true;
  }
  // Otherwise, check if the last password or magic link auth was more than 30 minutes ago.
  const lastAuthDate = primaryAuthFactors.reduce((acc, f) => {
    const date = parseJSON(f.last_authenticated_at);
    return isAfter(acc, date) ? date : acc;
  }, new Date());
  // If last auth was more than 30 minutes ago then password login is needed.
  return (
    differenceInMinutes(new Date(), lastAuthDate) >=
    STYTCH_EXTENDED_SESSION_DURATION_MINUTES
  );
};

export const isStytchFactorVerified = (it: { verified?: boolean }) =>
  it.verified;

export const isStytchMultiFactorSetupNeeded = (stytchUser: User) =>
  stytchUser.totps.filter(isStytchFactorVerified).length === 0 &&
  stytchUser.phone_numbers.filter(isStytchFactorVerified).length === 0 &&
  stytchUser.webauthn_registrations.filter(isStytchFactorVerified).length === 0;

export const isStytchMultiFactorLoginNeeded = (stytchSession: Session) => {
  // "Multi Factor" means any factor other than password or email magic link
  const isMultiFactor = (factor: AuthenticationFactor) =>
    !STYTCH_PRIMARY_AUTH_FACTOR_TYPES.includes(factor.type);
  return !stytchSession.authentication_factors.some(isMultiFactor);
};

// This is always 5 minutes according to
// https://stytch.com/docs/api/errors/401/session_too_old_to_reset_password
export const STYTCH_SESSION_PW_RESET_MAX_AGE_MINUTES = 5;
export const canStytchSessionResetPassword = (stytchSession?: Session | null) =>
  stytchSession?.authentication_factors.some(
    (factor) =>
      differenceInMinutes(new Date(), parseJSON(factor.last_authenticated_at)) <
      STYTCH_SESSION_PW_RESET_MAX_AGE_MINUTES,
  );

const ExpiredErrorTypes = [
  "session_too_old_to_reset_password",
  "unable_to_auth_magic_link",
  "expired_totp",
  "id_token_expired",
  "unable_to_auth_magic_link",
];

const InvalidCodeErrorTypes = [
  "unable_to_authenticate_totp",
  "invalid_code",
  "invalid_totp_code",
  "otp_code_not_found",
];

export const getStytchErrorMessage = (
  t: TFunction<["common", "auth"]>,
  error: unknown,
) => {
  if (isStytchApiError(error)) {
    if (ExpiredErrorTypes.includes(error.error_type)) {
      return t("auth:errors.expired");
    }
    if (InvalidCodeErrorTypes.includes(error.error_type)) {
      return t("auth:errors.incorrectCode");
    }
    if (error.error_type === "email_not_found") {
      return t("auth:errors.emailNotFound");
    }
    if (error.error_type === "unauthorized_credentials") {
      return t("auth:errors.incorrectPassword");
    }
    return error.error_message;
  }
  return getErrorMessage(t, error);
};

export const isStytchApiError = (error: unknown): error is StytchAPIError =>
  Boolean(
    error &&
      typeof error === "object" &&
      "name" in error &&
      error.name === "StytchAPIError",
  );

export const isStytchSdkApiError = (
  error: unknown,
): error is StytchSDKAPIError =>
  Boolean(
    error &&
      typeof error === "object" &&
      "name" in error &&
      error.name === "StytchSDKAPIError",
  );

export const useStytchFormErrorHandler = () => {
  const toaster = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "auth"]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useCallback<FormOnErrorFn<any>>(
    (error, _, { form }) => {
      if (isStytchApiError(error)) {
        log.error("Stytch error", error);
        if (ExpiredErrorTypes.includes(error.error_type)) {
          toaster.showErrorToast(getStytchErrorMessage(t, error));

          // Reset form to avoid triggering nav blocker
          form.reset(undefined, { keepValues: true });
          // Wait for form to reset
          setTimeout(() => {
            navigate("/auth/logout", { replace: true });
          }, 100);
        } else if (
          form.getValues("code") &&
          (InvalidCodeErrorTypes.includes(error.error_type) ||
            error.error_type === "unauthorized_credentials")
        ) {
          form.setError("code", { message: t("auth:errors.incorrectCode") });
        } else if (
          form.getValues("password") &&
          error.error_type === "unauthorized_credentials"
        ) {
          form.setError("password", {
            message: t("auth:errors.incorrectPassword"),
          });
        } else {
          toaster.showErrorToast(getStytchErrorMessage(t, error));
        }
        return true;
      }
    },
    [toaster, navigate, t],
  );
};

export const useStytchToken = () => {
  // The URL generated by Stytch will have a token query param
  // https://stytch.com/dashboard/redirect-urls
  const params = new URLSearchParams(window.location.search);
  return params.get("token")!;
};

export const useStytchUserAuthFactorInstances = (): AuthFactorInstance[] => {
  const { user: stytchUser } = useStytchUser();

  if (!stytchUser) {
    return [];
  }

  const activeTotps = stytchUser.totps.filter(isStytchFactorVerified);
  const activePhones = stytchUser.phone_numbers.filter(isStytchFactorVerified);
  const activeWebauthns = stytchUser.webauthn_registrations.filter(
    isStytchFactorVerified,
  );

  return [
    ...activeTotps.map(
      (data): AuthFactorInstance => ({
        type: "totp",
        id: data.totp_id,
        data,
      }),
    ),
    ...activePhones.map((data): AuthFactorInstance => {
      const phone = parsePhoneNumber(data.phone_number);
      return {
        type: "otp",
        id: data.phone_id,
        name: phone.formatNational(),
        data,
      };
    }),
    // Can't specify a specific webauthn device so just show one entry
    ...activeWebauthns.slice(0, 1).map(
      (data): AuthFactorInstance => ({
        type: "webauthn",
        id: data.webauthn_registration_id,
        data,
      }),
    ),
  ];
};

export const isStytchSessionExpired = (stytchSession: Session) =>
  isBefore(parseJSON(stytchSession.expires_at), new Date());

export const isStytchSessionExtended = (
  stytchSession: Session | null | undefined,
) =>
  Boolean(
    stytchSession &&
      differenceInDays(
        parseJSON(stytchSession.expires_at),
        parseJSON(stytchSession.started_at),
      ) === STYTCH_EXTENDED_SESSION_DURATION_DAYS,
  );
